import { supabase } from './supabase';

export interface DoctorDepartment {
  id: string;
  name: string;
  description?: string;
  status: 'active' | 'inactive';
  created_at: string;
  updated_at: string;
}

export interface BloodGroup {
  code: string;
  name: string;
  active: boolean;
  sort_order: number;
  created_at: string;
}

export interface DoctorCommissionCategory {
  code: string;
  name: string;
  description?: string;
  active: boolean;
  sort_order: number;
  created_at: string;
}

export interface DoctorCommissionRule {
  id: string;
  doctor_id: string;
  category_code: string;
  percent: number;
  effective_from: string;
  effective_to?: string;
  created_at: string;
  updated_at: string;
}

export interface DoctorCommissionLedger {
  id: string;
  doctor_id: string;
  entry_type: 'accrual' | 'payout' | 'adjustment';
  source_type?: string;
  source_id?: string;
  category_code?: string;
  amount: number;
  notes?: string;
  occurred_at: string;
  created_at: string;
}

export interface DoctorCommissionBalance {
  doctor_id: string;
  total_accrued: number;
  total_paid: number;
  total_adjustments: number;
  balance: number;
}

export interface DoctorBreak {
  id: string;
  doctor_id: string;
  every_day: boolean;
  day_of_week?: number;
  date?: string;
  break_from: string;
  break_to: string;
  created_at: string;
  updated_at: string;
}

export interface DoctorHoliday {
  id: string;
  doctor_id: string;
  date: string;
  reason?: string;
  created_at: string;
}

export interface EnhancedDoctor {
  id: string;
  name: string;
  email: string;
  phone?: string;
  specialization: string;
  department?: string;
  department_id?: string;
  blood_group?: string;
  qualification?: string;
  photo_url?: string;
  signature_url?: string;
  status: string;
  default_commission_percent: number;
  per_patient_minutes: number;
  is_report_doctor: boolean;
  report_designation?: string;
  report_description?: string;
  commission_balance?: number;
}

export const departmentsService = {
  async getAll() {
    const { data, error } = await supabase
      .from('doctor_departments')
      .select('*')
      .order('name');
    
    if (error) throw error;
    return data as DoctorDepartment[];
  },

  async getById(id: string) {
    const { data, error } = await supabase
      .from('doctor_departments')
      .select('*')
      .eq('id', id)
      .single();
    
    if (error) throw error;
    return data as DoctorDepartment;
  },

  async create(department: Omit<DoctorDepartment, 'id' | 'created_at' | 'updated_at'>) {
    const { data, error } = await supabase
      .from('doctor_departments')
      .insert([department])
      .select()
      .single();
    
    if (error) throw error;
    return data as DoctorDepartment;
  },

  async update(id: string, department: Partial<DoctorDepartment>) {
    const { data, error } = await supabase
      .from('doctor_departments')
      .update({ ...department, updated_at: new Date().toISOString() })
      .eq('id', id)
      .select()
      .single();
    
    if (error) throw error;
    return data as DoctorDepartment;
  },

  async delete(id: string) {
    const { error } = await supabase
      .from('doctor_departments')
      .delete()
      .eq('id', id);
    
    if (error) throw error;
  },
};

export const bloodGroupsService = {
  async getAll() {
    const { data, error } = await supabase
      .from('blood_groups')
      .select('*')
      .order('sort_order');
    
    if (error) throw error;
    return data as BloodGroup[];
  },

  async update(code: string, bloodGroup: Partial<BloodGroup>) {
    const { data, error } = await supabase
      .from('blood_groups')
      .update(bloodGroup)
      .eq('code', code)
      .select()
      .single();
    
    if (error) throw error;
    return data as BloodGroup;
  },
};

export const commissionService = {
  async getCategories() {
    const { data, error } = await supabase
      .from('doctor_commission_categories')
      .select('*')
      .eq('active', true)
      .order('sort_order');
    
    if (error) throw error;
    return data as DoctorCommissionCategory[];
  },

  async getDoctorRules(doctorId: string) {
    const { data, error } = await supabase
      .from('doctor_commission_rules')
      .select('*, category:doctor_commission_categories(*)')
      .eq('doctor_id', doctorId)
      .is('effective_to', null)
      .order('category_code');
    
    if (error) throw error;
    return data;
  },

  async upsertRule(rule: Omit<DoctorCommissionRule, 'id' | 'created_at' | 'updated_at'>) {
    const { data: existing } = await supabase
      .from('doctor_commission_rules')
      .select('id')
      .eq('doctor_id', rule.doctor_id)
      .eq('category_code', rule.category_code)
      .is('effective_to', null)
      .single();

    if (existing) {
      const { data, error } = await supabase
        .from('doctor_commission_rules')
        .update({ percent: rule.percent, updated_at: new Date().toISOString() })
        .eq('id', existing.id)
        .select()
        .single();
      
      if (error) throw error;
      return data;
    } else {
      const { data, error } = await supabase
        .from('doctor_commission_rules')
        .insert([rule])
        .select()
        .single();
      
      if (error) throw error;
      return data;
    }
  },

  async getBalance(doctorId: string) {
    const { data, error } = await supabase
      .from('doctor_commission_balances')
      .select('*')
      .eq('doctor_id', doctorId)
      .single();
    
    if (error && error.code !== 'PGRST116') throw error;
    return data as DoctorCommissionBalance | null;
  },

  async getLedger(doctorId: string) {
    const { data, error } = await supabase
      .from('doctor_commission_ledger')
      .select('*, category:doctor_commission_categories(*)')
      .eq('doctor_id', doctorId)
      .order('occurred_at', { ascending: false });
    
    if (error) throw error;
    return data;
  },
};

export const breaksService = {
  async getAll(doctorId?: string) {
    let query = supabase
      .from('doctor_breaks')
      .select('*, doctor:doctors(name)');
    
    if (doctorId) {
      query = query.eq('doctor_id', doctorId);
    }
    
    const { data, error } = await query.order('created_at', { ascending: false });
    
    if (error) throw error;
    return data;
  },

  async create(breakData: Omit<DoctorBreak, 'id' | 'created_at' | 'updated_at'>) {
    const { data, error } = await supabase
      .from('doctor_breaks')
      .insert([breakData])
      .select()
      .single();
    
    if (error) throw error;
    return data as DoctorBreak;
  },

  async delete(id: string) {
    const { error } = await supabase
      .from('doctor_breaks')
      .delete()
      .eq('id', id);
    
    if (error) throw error;
  },
};

export const holidaysService = {
  async getAll(doctorId?: string) {
    let query = supabase
      .from('doctor_holidays')
      .select('*, doctor:doctors(name)');
    
    if (doctorId) {
      query = query.eq('doctor_id', doctorId);
    }
    
    const { data, error } = await query.order('date', { ascending: false });
    
    if (error) throw error;
    return data;
  },

  async create(holiday: Omit<DoctorHoliday, 'id' | 'created_at'>) {
    const { data, error } = await supabase
      .from('doctor_holidays')
      .insert([holiday])
      .select()
      .single();
    
    if (error) throw error;
    return data as DoctorHoliday;
  },

  async delete(id: string) {
    const { error } = await supabase
      .from('doctor_holidays')
      .delete()
      .eq('id', id);
    
    if (error) throw error;
  },
};

export const enhancedDoctorsService = {
  async getAll(searchTerm?: string) {
    let query = supabase
      .from('doctors')
      .select(`
        *,
        department_info:doctor_departments(name),
        blood_group_info:blood_groups(name)
      `);
    
    if (searchTerm) {
      query = query.or(`name.ilike.%${searchTerm}%,email.ilike.%${searchTerm}%,phone.ilike.%${searchTerm}%`);
    }
    
    const { data, error } = await query.order('name');
    
    if (error) throw error;
    
    const doctorIds = data.map(d => d.id);
    const { data: balances } = await supabase
      .from('doctor_commission_balances')
      .select('*')
      .in('doctor_id', doctorIds);
    
    const balanceMap = new Map(balances?.map(b => [b.doctor_id, b.balance]) || []);
    
    return data.map(d => ({
      ...d,
      commission_balance: balanceMap.get(d.id) || 0,
    })) as EnhancedDoctor[];
  },

  async getReportDoctors() {
    const { data, error } = await supabase
      .from('doctors')
      .select('*')
      .eq('is_report_doctor', true)
      .order('name');
    
    if (error) throw error;
    return data as EnhancedDoctor[];
  },

  async updateReportDoctor(id: string, reportData: {
    is_report_doctor: boolean;
    report_designation?: string;
    report_description?: string;
    signature_url?: string;
  }) {
    const { data, error } = await supabase
      .from('doctors')
      .update({ ...reportData, updated_at: new Date().toISOString() })
      .eq('id', id)
      .select()
      .single();
    
    if (error) throw error;
    return data as EnhancedDoctor;
  },
};
